import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Search, MapPin, Phone, Globe, CheckCircle, AlertCircle,
  Shield, Lock, ChevronRight, ArrowRight, Star, Sparkles,
  UserCheck, BadgeCheck,
} from "lucide-react";
import { recordSearch } from "@/lib/recordSearch";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RotatingBanners from "@/components/RotatingBanners";
import { getIndustryType, exclusivityLabel } from "@/constants/industries";
import SEOHead from "@/components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function ProviderAvatar({ name, type }) {
  const industry = getIndustryType(type);
  const initials = (name || "")
    .split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("") || "PR";
  return (
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-white text-lg flex-shrink-0 shadow-sm"
      style={{ background: `linear-gradient(135deg, ${industry.color}, ${industry.color}88)` }}
    >
      {initials}
    </div>
  );
}

export default function FindProvider() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [zip, setZip] = useState(searchParams.get("zip") || "");
  const [inputZip, setInputZip] = useState(searchParams.get("zip") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [leadForm, setLeadForm] = useState(null);
  const [leadData, setLeadData] = useState({ taxpayer_name: "", taxpayer_phone: "", taxpayer_email: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [zipInfo, setZipInfo] = useState(null);
  const [zipBanners, setZipBanners] = useState([]);

  useEffect(() => {
    if (zip) searchProviders(zip);
  }, [zip]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchProviders = async (z) => {
    setLoading(true);
    setSearched(true);
    setResults([]);
    setZipInfo(null);
    setZipBanners([]);
    try {
      const [provRes, zipRes, bannerRes] = await Promise.allSettled([
        axios.get(`${API}/providers/search?zip=${z}`),
        axios.get(`${API}/zips/lookup?zip=${z}`),
        axios.get(`${API}/marketplace/banners?zip=${z}`),
      ]);
      if (provRes.status === "fulfilled") setResults(provRes.value.data.results || []);
      if (zipRes.status === "fulfilled") setZipInfo(zipRes.value.data);
      if (bannerRes.status === "fulfilled") setZipBanners(bannerRes.value.data.banners || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputZip.trim().length === 5) {
      recordSearch(inputZip.trim());           // a submitted search; the load effect below deliberately does not count
      setZip(inputZip.trim());
      navigate(`/find-provider?zip=${inputZip.trim()}`);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadError("");
    try {
      // leadForm holds the id of the listing being contacted: route to that listing, not to "whoever is in the ZIP"
      await axios.post(`${API}/leads`, { zip_code: zip, claim_id: leadForm, ...leadData });
      setLeadSent(true);
      setLeadForm(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLeadError(typeof d === "string" ? d : "Failed to send. Please try again.");
    }
  };

  const locationLabel = zipInfo ? `${zipInfo.city}, ${zipInfo.state}` : `ZIP ${zip}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEOHead
        title={
          searched && zipInfo
            ? `Tax Professionals in ${zipInfo.city}, ${zipInfo.state} (ZIP ${zip})`
            : "Find a Local Tax Professional Near Me — Search by ZIP Code"
        }
        description={
          searched && zipInfo
            ? `Find local tax professionals in ${zipInfo.city}, ${zipInfo.state} (ZIP ${zip}) who offer next-day refund advances. Free directory. 2027 tax season.`
            : "Search your ZIP code to find local tax professionals, debt relief, insurance agents, and more near you. Free."
        }
        canonical="/find-provider"
      />
      <Navbar />

      {/* ── Hero Search ── */}
      <section className="bg-[#003366] pt-12 pb-16">
        <div className="section-container max-w-3xl text-center">
          <p className="inline-flex items-center gap-1.5 text-[#2ECC71] text-xs font-bold uppercase tracking-widest mb-3">
            <Star size={12} fill="currentColor" /> Free to Search
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-3">
            Find a Provider
          </h1>
          <p className="text-slate-300 text-lg mb-8">
            Discover local tax professionals and service providers in your area.
          </p>

          {/* Pill search bar */}
          <form
            onSubmit={handleSearch}
            className="flex items-center gap-0 bg-white rounded-2xl shadow-2xl overflow-hidden max-w-xl mx-auto border-2 border-transparent focus-within:border-[#2ECC71]/40 transition-all"
            data-testid="provider-search-form"
          >
            <div className="relative flex-1">
              <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Enter your 5-digit ZIP code"
                value={inputZip}
                onChange={e => setInputZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                className="w-full h-14 pl-11 pr-4 text-[#0F172A] font-medium text-base bg-transparent focus:outline-none"
                maxLength={5}
                data-testid="provider-zip-input"
              />
            </div>
            <button
              type="submit"
              className="h-14 px-6 bg-[#2ECC71] hover:bg-[#27AE60] text-white font-bold flex items-center gap-2 transition-colors flex-shrink-0"
              data-testid="provider-search-submit"
            >
              <Search size={18} />
              <span className="hidden sm:inline">Search</span>
            </button>
          </form>

          {/* Trust strip */}
          <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
            {["One per ZIP · exclusive"].map(t => (
              <div key={t} className="flex items-center gap-1.5 text-slate-400 text-xs">
                <CheckCircle size={12} className="text-[#2ECC71]" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Results ── */}
      <section className="py-10">
        <div className="section-container max-w-3xl">

          {/* Loading */}
          {loading && (
            <div className="text-center py-16" data-testid="search-loading">
              <div className="w-10 h-10 border-3 border-[#003366] border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{borderWidth: "3px"}} />
              <p className="text-slate-500 text-sm">Searching providers in {zip}…</p>
            </div>
          )}

          {/* ── No providers found (unclaimed ZIP) ── */}
          {!loading && searched && results.length === 0 && (
            <div data-testid="no-results">
              {/* Taxpayer message */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={20} className="text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-amber-900 mb-1">
                    No Providers in {locationLabel} Yet
                  </h2>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    This ZIP code hasn't been claimed yet. Check back soon, or try a neighboring ZIP code.
                  </p>
                </div>
              </div>

              {/* FOMO CTA */}
              <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
                <div className="flex items-center gap-2 text-[#2ECC71] text-xs font-bold uppercase tracking-widest mb-3">
                  <Sparkles size={13} /> For Tax Professionals & Service Providers
                </div>
                <h3 className="text-2xl font-black text-[#0F172A] mb-2">
                  Be the First in ZIP {zip}{zipInfo ? ` — ${zipInfo.city}, ${zipInfo.state}` : ""}
                </h3>
                <p className="text-slate-500 text-sm mb-6 leading-relaxed max-w-md">
                  This is a <strong className="text-[#003366]">rare opportunity.</strong> Claim exclusive rights to this ZIP — every taxpayer who searches here goes directly to you.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/claim?zip=${zip}`}
                    className="inline-flex items-center justify-center gap-2 bg-[#003366] hover:bg-[#002244] text-white font-bold py-3 px-6 rounded-xl transition-colors"
                    data-testid="no-results-claim-cta"
                  >
                    Claim ZIP {zip} — $150 <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/advertise"
                    className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-6 rounded-xl transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* ── Providers Found ── */}
          {!loading && results.length > 0 && (
            <div data-testid="search-results">

              {/* Claimed Territory Banner */}
              <div
                className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-6 flex items-start sm:items-center gap-4 flex-col sm:flex-row"
                data-testid="claimed-territory-banner"
              >
                <div className="w-12 h-12 bg-[#2ECC71] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                  <BadgeCheck size={24} className="text-white" />
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
                      Territory — Claimed
                    </span>
                  </div>
                  <h2 className="text-lg font-black text-emerald-900">
                    {results.length} Provider{results.length !== 1 ? "s" : ""} Serving {locationLabel}
                  </h2>
                  <p className="text-emerald-700 text-sm mt-0.5">
                    This territory is exclusively held by a Verified Provider.
                  </p>
                </div>
                <Link
                  to="/claim"
                  className="flex-shrink-0 text-xs font-bold text-[#003366] hover:text-[#2ECC71] flex items-center gap-1 whitespace-nowrap transition-colors"
                  data-testid="claim-your-zip-link"
                >
                  <Lock size={12} /> Check Your ZIP <ChevronRight size={12} />
                </Link>
              </div>

              {/* Sponsored Banners — light theme */}
              {zipBanners.length > 0 && (
                <div
                  className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm"
                  data-testid="zip-banners-section"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-bold tracking-widest uppercase text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                      Sponsored · Local Businesses in {locationLabel}
                    </span>
                  </div>
                  <RotatingBanners
                    banners={zipBanners}
                    compact={true}
                    light={true}
                    onCTAClick={(b) => {
                      if (b.cta_url) window.open(b.cta_url, "_blank", "noopener");
                    }}
                  />
                </div>
              )}

              {/* Provider Cards */}
              <div className="space-y-4">
                {results.map((p) => (
                  <div
                    key={p.id}
                    className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                    data-testid={`provider-card-${p.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <ProviderAvatar name={p.business_name} type={p.advertiser_type} />
                      <div className="flex-1 min-w-0">
                        {/* Name + badge */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-lg font-black text-[#003366]">{p.business_name}</h3>
                          {exclusivityLabel(p.advertiser_type, p.zip_code) && (
                            <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
                              <UserCheck size={11} /> {exclusivityLabel(p.advertiser_type, p.zip_code)}
                            </span>
                          )}
                        </div>

                        {/* Meta row */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-500 text-sm mb-2">
                          {(p.city || p.state) && (
                            <span className="flex items-center gap-1.5">
                              <MapPin size={13} className="text-slate-400" />
                              {p.city && p.state ? `${p.city}, ${p.state}` : "Local Area"}
                            </span>
                          )}
                          {p.phone && (
                            <a
                              href={`tel:${p.phone}`}
                              className="flex items-center gap-1.5 text-[#003366] font-semibold hover:text-[#2ECC71] transition-colors"
                              data-testid={`provider-phone-${p.id}`}
                            >
                              <Phone size={13} /> {p.phone}
                            </a>
                          )}
                          {p.website && (
                            <a
                              href={p.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-[#2ECC71] font-semibold hover:underline"
                              data-testid={`provider-website-${p.id}`}
                            >
                              <Globe size={13} /> Visit Website
                            </a>
                          )}
                        </div>

                        {p.description && (
                          <p className="text-slate-500 text-sm leading-relaxed">{p.description}</p>
                        )}
                      </div>

                      {/* CTA */}
                      <button
                        onClick={() => { setLeadForm(p.id); setLeadSent(false); }}
                        className="flex-shrink-0 hidden sm:flex items-center gap-2 bg-[#003366] hover:bg-[#002244] text-white text-sm font-bold py-2.5 px-5 rounded-xl transition-colors"
                        data-testid={`contact-provider-btn-${p.id}`}
                      >
                        Contact <ArrowRight size={14} />
                      </button>
                    </div>

                    {/* Mobile CTA */}
                    <button
                      onClick={() => { setLeadForm(p.id); setLeadSent(false); }}
                      className="sm:hidden w-full mt-4 flex items-center justify-center gap-2 bg-[#003366] hover:bg-[#002244] text-white text-sm font-bold py-3 rounded-xl transition-colors"
                      data-testid={`contact-provider-btn-mobile-${p.id}`}
                    >
                      Contact Provider <ArrowRight size={14} />
                    </button>

                    {/* Inline Lead Form */}
                    {leadForm === p.id && !leadSent && (
                      <form
                        onSubmit={handleLeadSubmit}
                        className="mt-5 border-t border-slate-100 pt-5 space-y-3"
                        data-testid="lead-form"
                      >
                        <h4 className="font-bold text-[#0F172A] text-sm flex items-center gap-2">
                          <Phone size={14} className="text-[#2ECC71]" /> Request a Callback
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            className="input-field"
                            placeholder="Your Name"
                            value={leadData.taxpayer_name}
                            onChange={e => setLeadData({...leadData, taxpayer_name: e.target.value})}
                            data-testid="lead-name-input"
                          />
                          <input
                            className="input-field"
                            placeholder="Phone Number"
                            value={leadData.taxpayer_phone}
                            onChange={e => setLeadData({...leadData, taxpayer_phone: e.target.value})}
                            data-testid="lead-phone-input"
                          />
                        </div>
                        <input
                          className="input-field"
                          placeholder="Email Address"
                          type="email"
                          value={leadData.taxpayer_email}
                          onChange={e => setLeadData({...leadData, taxpayer_email: e.target.value})}
                          data-testid="lead-email-input"
                        />
                        <textarea
                          className="input-field !h-20 py-3 resize-none"
                          placeholder="Message (optional)"
                          value={leadData.message}
                          onChange={e => setLeadData({...leadData, message: e.target.value})}
                          data-testid="lead-message-input"
                        />
                        {leadError && <p className="text-red-500 text-sm">{leadError}</p>}
                        <div className="flex gap-3">
                          <button
                            type="submit"
                            className="flex items-center gap-2 bg-[#2ECC71] hover:bg-[#27AE60] text-white font-bold py-2.5 px-5 rounded-xl transition-colors"
                            data-testid="lead-submit-btn"
                          >
                            <CheckCircle size={15} /> Send Request
                          </button>
                          <button
                            type="button"
                            onClick={() => setLeadForm(null)}
                            className="py-2.5 px-5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold text-sm transition-colors"
                            data-testid="lead-cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}

                    {leadSent && leadForm === null && (
                      <div className="mt-4 flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-xl px-4 py-3 text-sm font-semibold" data-testid="lead-success">
                        <CheckCircle size={16} className="text-[#2ECC71]" /> Your request was sent! The provider will contact you shortly.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Global lead success */}
          {leadSent && (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3 text-emerald-800 font-semibold" data-testid="global-lead-success">
              <CheckCircle size={20} className="text-[#2ECC71]" />
              Request sent! Your local provider will contact you shortly.
            </div>
          )}

          {/* Pre-search state */}
          {!searched && !loading && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-[#003366]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Search size={28} className="text-[#003366]" />
              </div>
              <h2 className="text-xl font-bold text-slate-700 mb-2">Enter your ZIP code above</h2>
              <p className="text-slate-400 text-sm">We'll find providers in your exact territory.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
